import axios, {AxiosResponse, ResponseType} from 'axios';
import OnScreenDebugger from './OnScreenDebuuger';
import { freshen } from './tokenRefresh';

const GRAPH_BASE = 'https://graph.microsoft.com/beta';

export default {
  //
  // Get details of user, and return as JSON
  // https://docs.microsoft.com/en-us/graph/api/user-get?view=graph-rest-1.0&tabs=http#response-1
  //
  async getSelf(token): Promise<any> {
    let resp = await callGraph(token, '/me', 'json');
    if (resp) {
      return resp.data;
    }
  },

  //
  // Get user's photo and return as a blob object URL
  // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  //
  async getPhoto(token: string, resolution: string = '240x240'): Promise<string> {
    let resp = await callGraph(token, `/me/photos/${resolution}/$value`, 'blob');
    // console.log(resp);
    if (resp) {
      return URL.createObjectURL(resp.data);
    }
  },
  async getUserPhoto(token: string, username: string, resolution: string = '240x240'): Promise<string> {
    let resp = await callGraph(token, `/users/${username}/photos/${resolution}/$value`, 'blob');
    // console.log(resp);
    if (resp) {
      return URL.createObjectURL(resp.data);
    }
  },
  async GetUser(token, username): Promise<any> {
    let resp = await callGraph(token, `/users/${username}`, 'json');
    if (resp) {
      return resp.data;
    }
  },
  //
  // Get user's group memberships
  // https://docs.microsoft.com/en-us/graph/api/group-memberships?view=graph-rest-1.0&tabs=http
  //
  async getGroups(token): Promise<any> {
    let resp = await callGraph(token, '/me/memberOf', 'json');
    if (resp) {
      return resp.data;
    }
  },
  async GetSpecificGroup(token, query): Promise<any> {
    if(!query || query === "") 
    {
      return null;
    }
    let resp = await callGraph(token, `/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=displayname eq '${query}'&$select=id`, 'json');    
    if (resp) {
      return resp.data;
    }
  },

  async GetGroupMemberships(token, query): Promise<any> {
    if(!query || query === "") 
    {
      return null;
    }

    let body = {
      requests: [
      ]
    };
    Object.keys(query).forEach((key) => {
      body.requests.push({
        id: key,
        method: 'GET',
        url: `/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=displayname eq '${query[key]}'&$select=id`,
        "headers": {
          "ConsistencyLevel": "eventual"
        }
      });
    });

    let resp = await callGraphPost(token, `/$batch`, 'json', body);    
    if (resp) {
      return resp.data;
    }
  },

  async Test(token): Promise<any> {
    let resp = await callGraph(token, `/me`, 'json');
    if (resp) {
      return resp.data;
    }
  },

  async GetChat(token, id):Promise<any> {
    let resp = await callGraph(token, `/me/chats/${id}`)
    if (resp) {
      return resp.data;
    }
  },
  async GetChatMembers(token, id):Promise<any> {
    let resp = await callGraph(token, `/chats/${id}/members`)
    if (resp) {
      return resp.data;
    }
  },

  async GetMeeting(token, weburl): Promise<any> {
    let resp = await callGraph(token, `/me/onlineMeetings/?$filter=JoinWebUrl eq '${weburl}'`, 'json')
    if (resp) {
      return resp.data
    }
  },

  async GetEvents(token): Promise<any> {
    let resp = await callGraph(token, `/me/calendar/events`, 'json')
    if (resp) {
      return resp.data
    }
  },

  // One calendar event by id - e.g. a meeting chat's onlineMeetingInfo.calendarEventId.
  async GetEvent(token, eventId: string): Promise<any> {
    let resp = await callGraph(token, `/me/events/${encodeURIComponent(eventId)}`, 'json')
    if (resp) {
      return resp.data
    }
  },

  //
  // Search for users
  // https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  //
  async searchUsers(token, searchString: string, max = 50): Promise<any> {
    let resp = await callGraph(
      `/users?$filter=startswith(displayName, '${searchString}') or startswith(userPrincipalName, '${searchString}')&$top=${max}`,
      'json',
    );
    if (resp) {
      return resp.data;
    }
  },
};

//
// Common fetch wrapper (private)
//
async function callGraph(token, apiPath: string, type: ResponseType = 'json'): Promise<any> {
  // Acquire an access token to call APIs (like Graph)
  // Safe to call repeatedly as MSAL caches tokens locally
  token = await freshen(token);

  let resp: AxiosResponse | Error = await axios
    .get(`${GRAPH_BASE}${apiPath}`, {
      headers: { authorization: `bearer ${token}`, ConsistencyLevel: 'eventual' },
      responseType: type,
    })
    .catch((err) => err);

  if (resp instanceof Error) {
    // throw new Error(`Call to ${GRAPH_BASE}${apiPath} failed: ${resp.message}`);
    // console.error(`Call to ${GRAPH_BASE}${apiPath} failed: ${resp.message}`);
    OnScreenDebugger.instance.log('TeamsAuthService.callGraph(); error: ' + JSON.stringify(resp));

    return null;
  }

  return resp;
}

async function callGraphPost(token, apiPath: string, type: ResponseType = 'json', data = {}): Promise<any> {
  // Acquire an access token to call APIs (like Graph)
  // Safe to call repeatedly as MSAL caches tokens locally
  token = await freshen(token);

  let resp: AxiosResponse | Error = await axios
    .post(`${GRAPH_BASE}${apiPath}`, data, {
      headers: { authorization: `bearer ${token}`, ConsistencyLevel: 'eventual' },
      responseType: type,
    })
    .catch((err) => err);

  if (resp instanceof Error) {
    // throw new Error(`Call to ${GRAPH_BASE}${apiPath} failed: ${resp.message}`);
    // console.error(`Call to ${GRAPH_BASE}${apiPath} failed: ${resp.message}`);
    OnScreenDebugger.instance.log('TeamsAuthService.callGraphPost(); error: ' + JSON.stringify(resp));

    return null;
  }

  return resp;
}